/**
 * Voti della partita (V27), la scheda Voti della partita conclusa: un voto da
 * 1 a 10 a mezzi punti per chi ha giocato, oppure nessuno, con i minuti
 * accanto al nome.
 *
 * La fonte sono le partecipazioni: chi e rimasto in panchina non compare. Il
 * Mister scrive, l'Admin legge, il Dirigente non ha la scheda. Si vota solo a
 * partita conclusa: riaperta, la pagina torna al timer e la scheda sparisce. Il
 * salvataggio e quello dei voti dell'allenamento (V15), una richiesta per
 * riga ritentata sugli errori di rete, e "—" toglie il voto.
 */

import { Link, Navigate, useParams } from 'react-router-dom';
import { puo } from '@calcio/shared';
import { toast } from 'sonner';
import { StarIcon, TriangleAlertIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { chiama } from '../api/client.js';
import { RigaVoto } from '../comuni/RigaVoto.js';
import { useDati } from '../comuni/dati.js';
import { BarraScheda, CardVista, StatoSalvataggio } from '../comuni/Riquadri.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useCodaRighe } from '../comuni/useCodaRighe.js';
import { mediaVoti } from '../comuni/voti.js';
import { coloreVoto } from '../giocatori/colori-stato.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import {
  type VotiPartita as DatiVoti,
  descriviPartecipazione,
  ordinaChiHaGiocato,
} from './partita.js';
import { Nota } from '../comuni/Nota.js';

export function VotiPartita() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const { utente } = useContesto();
  const legge = Boolean(ss && puo(utente.assegnazioni, 'voto.leggi', ss));
  const { dati, errore } = useDati<DatiVoti>(legge ? `/partite/${id}/voti` : '');

  if (!ss || !legge) return <NonTrovata />;
  if (errore) return <StatoCaricamento errore={errore} />;
  if (!dati || dati.partita.id !== id) return <StatoCaricamento errore={null} />;
  if (dati.partita.squadraStagioneId !== ss) {
    return <Navigate to={`/sq/${dati.partita.squadraStagioneId}/partite/${id}/voti`} replace />;
  }
  return (
    <Elenco
      key={id}
      ss={ss}
      voti={dati}
      puoScrivere={puo(utente.assegnazioni, 'voto.scrivi', ss)}
      opera={puo(utente.assegnazioni, 'timer.opera', ss)}
    />
  );
}

function Elenco({
  ss,
  voti,
  puoScrivere,
  opera,
}: {
  ss: string;
  voti: DatiVoti;
  puoScrivere: boolean;
  opera: boolean;
}) {
  const p = voti.partita;
  const scrive = puoScrivere && p.stato === 'conclusa';
  const giocatori = ordinaChiHaGiocato(voti.giocatori);

  const c = useCodaRighe<number>(
    () => Object.fromEntries(voti.giocatori.map((g) => [g.id, g.voto])),
    async (giocatoreId, valore) => {
      await chiama(
        `/partite/${p.id}/voti/${giocatoreId}`,
        valore === null ? { metodo: 'DELETE' } : { metodo: 'PUT', corpo: { valore } },
      );
    },
    (rifiuto) => toast.error(rifiuto),
  );

  const valori = giocatori.map((g) => c.valore(g.id));
  const valutati = valori.filter((v) => v !== null).length;
  const media = mediaVoti(valori);
  const inSospeso = c.inSospeso();
  const nonSalvate = giocatori.filter((g) => c.esito(g.id) === 'non_salvato').length;

  return (
    <>
      <BarraScheda>
        <span className="flex items-center gap-1 text-muted-foreground" title="Giocatori votati">
          <StarIcon aria-hidden className="size-4" />
          <span className="font-semibold text-foreground tabular-nums">
            {valutati}/{giocatori.length}
          </span>
          votati
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          Media
          {media !== null ? (
            <Badge className={cn('tabular-nums', coloreVoto(media))}>
              {media.toLocaleString('it-IT')}
            </Badge>
          ) : (
            <span className="font-semibold text-foreground">—</span>
          )}
        </span>
        {scrive && (
          <StatoSalvataggio
            inSospeso={inSospeso}
            nonSalvate={nonSalvate}
            onRiprova={() => c.riprova()}
          />
        )}
      </BarraScheda>

      {giocatori.length === 0 ? (
        <Alert>
          <TriangleAlertIcon />
          <AlertTitle>Nessuno risulta aver giocato</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              I voti si danno a chi ha una partecipazione. Le partecipazioni nascono dagli ingressi
              in campo registrati negli eventi, e si correggono correggendo gli eventi.
            </span>
            {opera && (
              <Button asChild variant="outline">
                <Link to={`/sq/${ss}/partite/${p.id}/modifica`}>Correggi gli eventi</Link>
              </Button>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <CardVista
          titolo="Chi ha giocato"
          Icona={StarIcon}
          conteggio={`${valutati}/${giocatori.length}`}
        >
          <Nota className="px-1">
            Il voto spetta a chi è stato in campo: chi è rimasto in panchina non compare.
            {scrive && ' "—" toglie il voto.'}
          </Nota>
          <ul className="grid gap-2 @3xl:grid-cols-2">
            {giocatori.map((g) => (
              <RigaVoto
                key={g.id}
                numero={g.numero}
                ruolo={g.ruolo}
                nome={`${g.cognome} ${g.nome}`}
                dettaglio={descriviPartecipazione(g)}
                esito={c.esito(g.id)}
                valore={c.valore(g.id)}
                scrive={scrive}
                onCambio={(v) => c.imposta(g.id, v)}
              />
            ))}
          </ul>
        </CardVista>
      )}
    </>
  );
}
